import React from 'react';
import { Box } from '@mui/material';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { parseRichText } from '../utils/richText';

/**
 * Renders mixed text, LaTeX (including \begin{} environments), and images.
 * Automatically converts HTML tags to LaTeX equivalents.
 */
const LatexRenderer = ({ text, fontSize, color, fontWeight, display = 'inline' }) => {
  if (text === null || text === undefined) return null;

  if (Array.isArray(text)) {
    return (
      <Box component={display === 'inline' ? 'span' : 'div'} sx={{ display }}>
        {text.map((item, i) => (
          <LatexRenderer key={i} text={item} fontSize={fontSize} color={color} fontWeight={fontWeight} display="inline" />
        ))}
      </Box>
    );
  }

  let str = '';
  if (typeof text === 'object') {
    str = text.text || text.question || text.content || text.label || text.questionText || '';
    if (!str) return null;
  } else {
    str = String(text);
  }

  const segments = parseRichText(str);
  if (segments.length === 0) return null;

  return (
    <Box
      component={display === 'inline' ? 'span' : 'div'}
      sx={{
        fontSize: fontSize || 'inherit',
        fontWeight: fontWeight || 'inherit',
        color: color || 'inherit',
        lineHeight: 1.7,
        display: display === 'inline' ? 'inline' : 'block',
        whiteSpace: 'pre-wrap',
        fontFamily: '"Outfit", "Roboto", "Helvetica", "Arial", sans-serif',
      }}
    >
      {segments.map((seg, idx) => {
        try {
          if (seg.type === 'displayMath') {
            // Display size but kept in the text flow (like the Flutter app), so it doesn't jump to a new line
            const html = katex.renderToString(`\\displaystyle ${seg.content}`, { displayMode: false, throwOnError: false, trust: true, strict: false });
            return <Box component="span" key={idx} sx={{ display: 'inline-block', maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden', verticalAlign: 'middle', mx: 0.2, py: 0.5 }} dangerouslySetInnerHTML={{ __html: html }} />;
          }
          if (seg.type === 'inlineMath') {
            const html = katex.renderToString(seg.content, { displayMode: false, throwOnError: false, trust: true, strict: false });
            // A bare sub/superscript (from <sub>/<sup>) must hug the preceding text: H₂O, not H ₂ O
            const hugs = /^\s*[_^]/.test(seg.content);
            return <Box component="span" key={idx} sx={{ verticalAlign: hugs ? 'baseline' : 'middle', mx: hugs ? 0 : 0.2 }} dangerouslySetInnerHTML={{ __html: html }} />;
          }
          if (seg.type === 'image') {
            return (
              <Box key={idx} component="img" src={seg.url} alt={seg.alt}
                sx={{ maxWidth: '100%', height: 'auto', display: 'block', my: 2, mx: 'auto', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
            );
          }
          return <span key={idx}>{seg.content}</span>;
        } catch (e) {
          return <span key={idx} style={{ color: '#e53e3e', fontSize: '0.85em' }}>{seg.content}</span>;
        }
      })}
    </Box>
  );
};

export default LatexRenderer;
